import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { memo, useContext, useMemo, useEffect } from 'react';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';
import { InstantSearch, Configure } from 'react-instantsearch-hooks-web';
import algoliasearch from 'algoliasearch/lite';
import SearchResultsHero from './SearchResultsHero';
import SearchResultsList from './SearchResultsList';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { getAlgoliaPermittedContentTypesFilter } from '../../utils/getAlgoliaPermittedContentTypesFilter/getAlgoliaPermittedContentTypesFilter.util';
import { getQueryParam } from '../../utils/getQueryParam/getQueryParam.util';

interface SearchResultsPageBlokProps extends Blok {
  hero_background_image: StoryblokAsset;
  hero_heading: string;
  hero_body?: string;
  hero_search_bar_placeholder: string;
  permitted_content_types: string[];
  number_of_results_per_page: string;
  pagination_footer_text?: string;
  no_results_found_title: string;
  no_results_found_body?: string;
  body?: Blok[] | null;
  transparent_header: boolean;
  show_contact_bar: boolean;
}

interface SearchResultsPageProps {
  blok: SearchResultsPageBlokProps;
}

const SearchResultsPage: React.FC<SearchResultsPageProps> = ({ blok }) => {
  const { header, footer, contact_bar } = useContext(LayoutContext);
  const {
    hero_background_image,
    hero_heading,
    hero_body = undefined,
    hero_search_bar_placeholder,
    permitted_content_types,
    number_of_results_per_page,
    pagination_footer_text = undefined,
    no_results_found_title,
    no_results_found_body = undefined,
    body,
    show_contact_bar,
  } = blok;

  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME!}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  /*-- 
    initially the plan was useState and useEffect, 
    but this didn't work with the InstantSearch 
    component for some reason
  --*/
  const getInitialQuery = (): string => {
    if (typeof window === 'undefined') return '';
    return getQueryParam('q') || '';
  };

  const getInitialPage = (): number => {
    if (typeof window === 'undefined') return 0;
    return parseInt(getQueryParam('page')!) || 0;
  };

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: blok.transparent_header,
        isHeaderSearchFunctionalityRequired: false,
        showContactBar: blok.show_contact_bar,
      }}
    >
      <div className="flex flex-col h-screen justify-between">
        {header && (
          <StoryblokComponent blok={header.content} key={header._uid} />
        )}
        <main className="mb-auto" {...storyblokEditable(blok)}>
          <InstantSearch
            indexName={indexName}
            searchClient={algoliaSearchClient}
            initialUiState={{
              [indexName]: {
                query: getInitialQuery(),
                page: getInitialPage(),
              },
            }}
          >
            <Configure
              filters={`${getAlgoliaPermittedContentTypesFilter(
                permitted_content_types
              )}`}
              hitsPerPage={parseInt(number_of_results_per_page)}
            />
            <SearchResultsHero
              hero_background_image={hero_background_image}
              hero_heading={hero_heading}
              hero_body={hero_body}
              hero_search_bar_placeholder={hero_search_bar_placeholder}
            />
            <SearchResultsList
              pagination_footer_text={pagination_footer_text}
              no_results_found_title={no_results_found_title}
              no_results_found_body={no_results_found_body}
            />
          </InstantSearch>
          {body?.map((nestedBlok: Blok) => (
            <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
          ))}
          {show_contact_bar && contact_bar && (
            <StoryblokComponent
              blok={contact_bar.content}
              key={contact_bar._uid}
            />
          )}
        </main>
        {footer && (
          <StoryblokComponent blok={footer.content} key={footer._uid} />
        )}
        <TestingGrid />
      </div>
    </PageContext.Provider>
  );
};

export default memo(SearchResultsPage);
